import { useState } from 'react';
import { DollarSign, TrendingDown, TrendingUp, Calendar, Fuel, Wrench, Users, FileText } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

interface CostEntry {
  id: string;
  date: string;
  category: 'Yakıt' | 'Bakım' | 'Personel' | 'Sigorta' | 'Bekleme' | 'Diğer';
  description: string;
  amount: number;
  vehiclePlate?: string;
  approved: boolean;
}

const costData: CostEntry[] = [
  {
    id: '1',
    date: '2024-03-14',
    category: 'Yakıt',
    description: 'Motorin - 34 ABC 123',
    amount: 8500,
    vehiclePlate: '34 ABC 123',
    approved: true,
  },
  {
    id: '2',
    date: '2024-03-13',
    category: 'Bakım',
    description: 'Periyodik Bakım - 06 XYZ 456',
    amount: 3200,
    vehiclePlate: '06 XYZ 456',
    approved: true,
  },
  {
    id: '3',
    date: '2024-03-13',
    category: 'Personel',
    description: 'Sürücü Maaşları - Mart',
    amount: 85000,
    approved: true,
  },
  {
    id: '4',
    date: '2024-03-12',
    category: 'Bekleme',
    description: 'Bekleme Süresi Maliyeti',
    amount: 1950,
    approved: true,
  },
  {
    id: '5',
    date: '2024-03-12',
    category: 'Yakıt',
    description: 'Motorin - 35 DEF 789',
    amount: 9200,
    vehiclePlate: '35 DEF 789',
    approved: true,
  },
  {
    id: '6',
    date: '2024-03-11',
    category: 'Sigorta',
    description: 'Kasko Poliçe Yenileme',
    amount: 12500,
    approved: false,
  },
  {
    id: '7',
    date: '2024-03-10',
    category: 'Bakım',
    description: 'Lastik Değişimi - 16 GHI 321',
    amount: 4800,
    vehiclePlate: '16 GHI 321',
    approved: true,
  },
];

const monthlyData = [
  { id: 'jan', month: 'Ocak', yakit: 45000, bakim: 12000, personel: 85000, diger: 18000 },
  { id: 'feb', month: 'Şubat', yakit: 48000, bakim: 8500, personel: 85000, diger: 15000 },
  { id: 'mar', month: 'Mart', yakit: 52000, bakim: 15500, personel: 85000, diger: 22000 },
];

export function CostAnalysis() {
  const [data] = useState<CostEntry[]>(costData);
  const [selectedPeriod, setSelectedPeriod] = useState('month');

  const totalCost = data.reduce((sum, entry) => sum + entry.amount, 0);
  const approvedCost = data.filter(e => e.approved).reduce((sum, entry) => sum + entry.amount, 0);
  const pendingCost = data.filter(e => !e.approved).reduce((sum, entry) => sum + entry.amount, 0);

  const costByCategory = [
    { name: 'Yakıt', value: data.filter(e => e.category === 'Yakıt').reduce((sum, e) => sum + e.amount, 0), color: '#F59E0B' },
    { name: 'Bakım', value: data.filter(e => e.category === 'Bakım').reduce((sum, e) => sum + e.amount, 0), color: '#3B82F6' },
    { name: 'Personel', value: data.filter(e => e.category === 'Personel').reduce((sum, e) => sum + e.amount, 0), color: '#10B981' },
    { name: 'Sigorta', value: data.filter(e => e.category === 'Sigorta').reduce((sum, e) => sum + e.amount, 0), color: '#8B5CF6' },
    { name: 'Bekleme', value: data.filter(e => e.category === 'Bekleme').reduce((sum, e) => sum + e.amount, 0), color: '#EF4444' },
    { name: 'Diğer', value: data.filter(e => e.category === 'Diğer').reduce((sum, e) => sum + e.amount, 0), color: '#6B7280' },
  ].filter(item => item.value > 0);

  const categoryIcons = {
    'Yakıt': Fuel,
    'Bakım': Wrench,
    'Personel': Users,
    'Sigorta': FileText,
    'Bekleme': Calendar,
    'Diğer': DollarSign,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Maliyet Analizi</h1>
          <p className="text-gray-500 mt-1">Operasyonel maliyetleri izleyin ve analiz edin</p>
        </div>
        <div className="flex items-center gap-3">
          <Calendar className="w-5 h-5 text-gray-400" />
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="week">Bu Hafta</option>
            <option value="month">Bu Ay</option>
            <option value="quarter">Bu Çeyrek</option>
            <option value="year">Bu Yıl</option>
          </select>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-lg shadow p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium opacity-90">Toplam Maliyet</h3>
            <DollarSign className="w-5 h-5 opacity-75" />
          </div>
          <p className="text-3xl font-bold">{totalCost.toLocaleString('tr-TR')} ₺</p>
          <p className="text-sm opacity-75 mt-1">Bu ay</p>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg shadow p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium opacity-90">Onaylandı</h3>
            <TrendingUp className="w-5 h-5 opacity-75" />
          </div>
          <p className="text-3xl font-bold">{approvedCost.toLocaleString('tr-TR')} ₺</p>
          <p className="text-sm opacity-75 mt-1">{data.filter(e => e.approved).length} kayıt</p>
        </div>

        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg shadow p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium opacity-90">Onay Bekliyor</h3>
            <TrendingDown className="w-5 h-5 opacity-75" />
          </div>
          <p className="text-3xl font-bold">{pendingCost.toLocaleString('tr-TR')} ₺</p>
          <p className="text-sm opacity-75 mt-1">{data.filter(e => !e.approved).length} kayıt</p>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg shadow p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium opacity-90">Ortalama/Gün</h3>
            <Calendar className="w-5 h-5 opacity-75" />
          </div>
          <p className="text-3xl font-bold">{Math.round(totalCost / 14).toLocaleString('tr-TR')} ₺</p>
          <p className="text-sm opacity-75 mt-1">Son 2 hafta</p>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Cost by Category - Pie Chart */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Kategoriye Göre Maliyet Dağılımı</h2>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={costByCategory}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {costByCategory.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip key="cost-tooltip" formatter={(value: number) => `${value.toLocaleString('tr-TR')} ₺`} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Monthly Trend */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Aylık Maliyet Trendi</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={monthlyData}>
              <CartesianGrid key="monthly-grid" strokeDasharray="3 3" />
              <XAxis key="monthly-xaxis" dataKey="month" />
              <YAxis key="monthly-yaxis" />
              <Tooltip key="monthly-tooltip" formatter={(value: number) => `${value.toLocaleString('tr-TR')} ₺`} />
              <Legend key="monthly-legend" />
              <Bar key="bar-yakit" dataKey="yakit" fill="#F59E0B" name="Yakıt" />
              <Bar key="bar-bakim" dataKey="bakim" fill="#3B82F6" name="Bakım" />
              <Bar key="bar-personel" dataKey="personel" fill="#10B981" name="Personel" />
              <Bar key="bar-diger" dataKey="diger" fill="#6B7280" name="Diğer" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Category Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {costByCategory.map((category) => {
          const Icon = categoryIcons[category.name as keyof typeof categoryIcons];
          return (
            <div key={category.name} className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 rounded-lg" style={{ backgroundColor: `${category.color}20` }}>
                  <Icon className="w-4 h-4" style={{ color: category.color }} />
                </div>
                <h3 className="text-sm font-medium text-gray-700">{category.name}</h3>
              </div>
              <p className="text-xl font-bold text-gray-900">{category.value.toLocaleString('tr-TR')} ₺</p>
              <p className="text-xs text-gray-500 mt-1">
                {((category.value / totalCost) * 100).toFixed(1)}% toplam
              </p>
            </div>
          );
        })}
      </div>

      {/* Detailed Cost Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Maliyet Kayıtları</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tarih
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Kategori
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Açıklama
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Araç
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tutar
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Durum
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {data.map((entry) => {
                const Icon = categoryIcons[entry.category];
                return (
                  <tr key={entry.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {new Date(entry.date).toLocaleDateString('tr-TR')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-900">{entry.category}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {entry.description}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {entry.vehiclePlate || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {entry.amount.toLocaleString('tr-TR')} ₺
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {entry.approved ? (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Onaylandı
                        </span>
                      ) : (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                          Bekliyor
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="bg-gray-50 border-t-2 border-gray-300">
              <tr>
                <td colSpan={4} className="px-6 py-4 text-sm font-bold text-gray-900 text-right">
                  Toplam:
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                  {totalCost.toLocaleString('tr-TR')} ₺
                </td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}